import BENEFICIARY_SUBJECT_TYPE_KEY from '@Domain/Subjects/BeneficiarySubjectTypeKey';
import buildSubjectListPath from '@UI/Pages/Beneficiaries/Shared/Helpers/buildSubjectListPath';
import FetchWorkingFacilityCounts from '@Application/Indicators/WorkingFacility/Queries/FetchWorkingFacilityCounts';
import fetchWorkingFacilityCounts from '@Application/Indicators/WorkingFacility/fetchWorkingFacilityCounts';
import buildIndicatorQueryKey from '@Application/Indicators/buildIndicatorQueryKey';
import useRefetchInterval, {
	SUPERVISION_REFETCH_INTERVAL_IN_MILLISECONDS,
} from '@UI/Hooks/useRefetchInterval';
import buildTileStyle from '@Guidelines/Component/IconTile/buildTileStyle';
import { theme } from '@Guidelines/Theme/theme';
import BeneficiaryFilterView from '@UI/Pages/Beneficiaries/BeneficiaryList/Filters/BeneficiaryFilterView';
import { setBeneficiaryFilters } from '@UI/Pages/Beneficiaries/BeneficiaryList/Filters/Signals/beneficiaryFiltersSignal';
import resolveBeneficiaryListScope from '@UI/Pages/Home/Helpers/resolveBeneficiaryListScope';
import useHomeIndicatorScope from '@UI/Pages/Home/Hooks/useHomeIndicatorScope';
import useRetainedIndicatorView from '@UI/Pages/Home/Hooks/useRetainedIndicatorView';
import homePeriodSignal from '@UI/Pages/Home/Signals/homePeriodSignal';
import WorkflowRunFilterView from '@UI/Pages/WorkflowRuns/Filters/WorkflowRunFilterView';
import { setWorkflowRunFilters } from '@UI/Pages/WorkflowRuns/Filters/Signals/workflowRunFiltersSignal';
import homeScopeSignal from '@UI/Pages/Home/Signals/homeScopeSignal';
import { PermissionEnum } from '@Application/Security/Roles';
import useHasPermission from '@UI/Providers/PermissionProvider/useHasPermission';
import WithPermission from '@UI/Providers/PermissionProvider/WithPermission';
import useStrictAuthenticatedUser from '@UI/Providers/AuthenticatedUserProvider/useStrictAuthenticatedUser';
import useCurrentFacility from '@UI/Providers/CurrentFacilityProvider/useCurrentFacility';
import useCurrentOrganization from '@UI/Providers/CurrentOrganizationProvider/useCurrentOrganization';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { useSignalValue } from '@verclam/pulsar';
import { useDispatch, useSelector, useStoreQuery } from '@verclam/synapse';
import { Button, Skeleton } from '@verclam/ui-kit';
import { Activity, ChevronRight, Users } from 'lucide-react';
import {
	buildCardStyle,
	captionRowStyle,
	captionStyle,
	cardFacilityStyle,
	cardFigureStyle,
	cardTextStyle,
	cardTitleStyle,
	chevronStyle,
	gridStyle,
	sectionStyle,
} from '@UI/Pages/Home/Shared/shortcutCardStyles';

const beneficiariesCardStyle = buildCardStyle(theme.home.shortcutTiles.beneficiaries);
const beneficiariesTileStyle = buildTileStyle(theme.home.shortcutTiles.beneficiaries);
const workflowRunsCardStyle = buildCardStyle(theme.home.shortcutTiles.workflowRuns);
const workflowRunsTileStyle = buildTileStyle(theme.home.shortcutTiles.workflowRuns);

/**
 * Entry points to the dossier and workflow-run lists. "Bénéficiaires" follows the home Périmètre
 * and reads the figure the beneficiaries block already loads; "Traitements" follows the working agency.
 */
const ShortcutCards: React.FC = () => {
	const dispatch = useDispatch();
	const { user } = useStrictAuthenticatedUser();
	const { currentFacility, selectFacility } = useCurrentFacility();
	const { organizations, selectOrganization } = useCurrentOrganization();
	const scope = useSignalValue(homeScopeSignal);
	const period = useSignalValue(homePeriodSignal);
	const { status: indicatorStatus, scope: indicatorHomeScope, indicatorScope } = useHomeIndicatorScope();
	const canReadWorkflowRuns = useHasPermission(PermissionEnum.WORKFLOW_RUN_FACILITY_READ);
	const facilityId = currentFacility?.id ?? null;

	const { data, error, refetch } = useStoreQuery({
		selector: (state) => state.indicatorsStore.workingFacilityCounts,
		stopOn: (view) => facilityId === null || (view !== null && view.facilityId === facilityId),
		query: () => {
			if (facilityId === null) {
				return Promise.resolve();
			}

			return dispatch(
				fetchWorkingFacilityCounts(
					new FetchWorkingFacilityCounts({
						facilityId,
						includeWatchedRuns: canReadWorkflowRuns,
					}),
				),
			);
		},
		deps: [facilityId, canReadWorkflowRuns],
	});

	useRefetchInterval(refetch, SUPERVISION_REFETCH_INTERVAL_IN_MILLISECONDS);

	const indicatorQueryKey = indicatorScope === null ? null : buildIndicatorQueryKey(indicatorScope, period);
	const beneficiaryIndicators = useSelector(
		(state) => state.indicatorsStore.beneficiaries,
		(previous, next) => previous === next,
	);
	const { view: beneficiaryIndicatorsView } = useRetainedIndicatorView(
		beneficiaryIndicators,
		indicatorQueryKey,
	);
	const beneficiaryListScope = React.useMemo(
		() => resolveBeneficiaryListScope(indicatorHomeScope, organizations),
		[indicatorHomeScope, organizations],
	);

	const counts = data !== null && data.facilityId === facilityId ? data : null;

	const otherFacility = React.useMemo(() => {
		if (scope.kind !== 'facility' || scope.id === facilityId) {
			return null;
		}

		for (const organization of organizations) {
			const facility = organization.facilities.find((candidate) => candidate.id === scope.id);

			if (facility) {
				return { facility, organizationId: organization.id };
			}
		}

		return null;
	}, [scope, facilityId, organizations]);

	const handleBeneficiariesClick = React.useCallback(() => {
		setBeneficiaryFilters(
			BeneficiaryFilterView.createDefault().setAgencyScope(
				beneficiaryListScope.scope,
				beneficiaryListScope.facilityIds,
			),
		);
	}, [beneficiaryListScope]);

	const handleWorkflowRunsClick = React.useCallback(() => {
		if (facilityId === null) {
			return;
		}

		// The screen validates its stored filters against the agency and the caller: posting both
		// makes it open on "À surveiller", the figure shown on the card.
		setWorkflowRunFilters(WorkflowRunFilterView.createDefault(facilityId, user.id).setStatus('watch'));
	}, [facilityId, user.id]);

	const handleOpenOtherFacility = React.useCallback(() => {
		if (otherFacility === null) {
			return;
		}

		selectOrganization(otherFacility.organizationId);
		selectFacility(otherFacility.facility.id, { redirectTo: '/' });
	}, [otherFacility, selectOrganization, selectFacility]);

	const renderFigure = (text: string | null): React.ReactNode => {
		if (text === null) {
			return null;
		}

		return <span css={cardFigureStyle}>{text}</span>;
	};

	const hasFailed = error !== null && facilityId !== null;
	const watchedRuns = counts?.watchedRuns ?? 0;

	const facilityLine = currentFacility === null ? null : `Agence ${currentFacility.name}`;
	const isLoading = counts === null && facilityId !== null && error === null;
	const isBeneficiariesLoading = indicatorStatus !== 'unavailable' && beneficiaryIndicatorsView === null;
	const beneficiariesFigure =
		indicatorStatus === 'unavailable' || beneficiaryIndicatorsView === null
			? null
			: `${beneficiaryIndicatorsView.active} ${beneficiaryIndicatorsView.active === 1 ? 'actif' : 'actifs'}`;
	const workflowRunsFigure =
		counts === null || counts.watchedRuns === null
			? null
			: watchedRuns === 0
				? 'Aucun à surveiller'
				: `${watchedRuns} à surveiller`;
	const buildAriaLabel = (
		title: string,
		figure: string | null,
		place: string | null,
	): string | undefined => {
		if (figure === null || place === null) {
			return undefined;
		}

		return `${title} : ${figure.toLowerCase()}, ${place}`;
	};
	const workflowRunsFigureShown = error === null ? workflowRunsFigure : null;

	return (
		<section css={sectionStyle}>
			<nav aria-label="Raccourcis" css={gridStyle}>
				<Link
					to={buildSubjectListPath(BENEFICIARY_SUBJECT_TYPE_KEY)}
					css={beneficiariesCardStyle}
					onClick={handleBeneficiariesClick}
					aria-label={buildAriaLabel(
						'Bénéficiaires',
						beneficiariesFigure,
						beneficiaryListScope.label,
					)}
				>
					<span css={beneficiariesTileStyle} aria-hidden="true">
						<Users size={20} />
					</span>
					<span css={cardTextStyle}>
						<span css={cardTitleStyle}>Bénéficiaires</span>
						{isBeneficiariesLoading ? (
							<Skeleton width="6rem" height="1.097rem" />
						) : (
							renderFigure(beneficiariesFigure)
						)}
						<span css={cardFacilityStyle}>{beneficiaryListScope.label}</span>
					</span>
					<span css={chevronStyle} aria-hidden="true">
						<ChevronRight size={16} />
					</span>
				</Link>
				<WithPermission permission={PermissionEnum.WORKFLOW_RUN_FACILITY_READ}>
					<Link
						to="/workflow-runs"
						css={workflowRunsCardStyle}
						onClick={handleWorkflowRunsClick}
						aria-label={buildAriaLabel(
							'Traitements',
							workflowRunsFigureShown,
							currentFacility === null ? null : `agence ${currentFacility.name}`,
						)}
					>
						<span css={workflowRunsTileStyle} aria-hidden="true">
							<Activity size={20} />
						</span>
						<span css={cardTextStyle}>
							<span css={cardTitleStyle}>Traitements</span>
							{isLoading ? (
								<Skeleton width="6rem" height="1.097rem" />
							) : (
								renderFigure(workflowRunsFigureShown)
							)}
							{facilityLine !== null && <span css={cardFacilityStyle}>{facilityLine}</span>}
						</span>
						<span css={chevronStyle} aria-hidden="true">
							<ChevronRight size={16} />
						</span>
					</Link>
				</WithPermission>
			</nav>
			{hasFailed && (
				<div css={captionRowStyle}>
					<p css={captionStyle}>Chiffres indisponibles pour le moment.</p>
					<Button type="button" link label="Réessayer" onClick={refetch} />
				</div>
			)}
			{currentFacility === null && <p css={captionStyle}>Aucune agence de travail sélectionnée</p>}
			{otherFacility !== null && (
				<div css={captionRowStyle}>
					<p css={captionStyle}>Le raccourci Traitements porte sur votre agence de travail.</p>
					<Button
						type="button"
						link
						label={`Ouvrir ${otherFacility.facility.name}`}
						onClick={handleOpenOtherFacility}
					/>
				</div>
			)}
		</section>
	);
};

export default ShortcutCards;
